import React from "react";
import { Card } from "../ui/card";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import { History, FileText, Upload, ArrowRight, Clock } from "lucide-react";

// Translations
const translations = {
  nb: {
    noChangesYet: "Ingen endringer ennå",
    changesDescription: "Endringer du gjør med Lexy vil vises her",
    uploaded: "Lastet opp",
    inserted: "Satt inn",
    tracked: "Sporet",
    comment: "Kommentar",
    today: "I dag"
  },
  en: {
    noChangesYet: "No changes yet",
    changesDescription: "Changes you make with Lexy will appear here",
    uploaded: "Uploaded",
    inserted: "Inserted",
    tracked: "Tracked",
    comment: "Comment",
    today: "Today"
  }
};

export const ChangesTab = ({ changes, language = 'nb' }) => {
  const t = translations[language] || translations.nb;
  
  const getIcon = (type) => {
    switch (type) {
      case "upload": return Upload;
      case "direct": return ArrowRight;
      default: return FileText;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case "upload": return t.uploaded;
      case "direct": return t.inserted;
      case "tracked": return t.tracked;
      case "comment": return t.comment;
      default: return type;
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const locale = language === 'en' ? 'en-US' : 'nb-NO';
    return date.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const locale = language === 'en' ? 'en-US' : 'nb-NO';
    if (date.toDateString() === today.toDateString()) return t.today;
    return date.toLocaleDateString(locale, { day: "numeric", month: "short" });
  };

  return (
    <div className="h-full flex flex-col bg-white" data-testid="changes-tab">
      {changes.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
          <div className="w-14 h-14 rounded-2xl bg-violet-100 flex items-center justify-center mb-4">
            <History className="w-7 h-7 text-violet-600" />
          </div>
          <h3 className="font-medium text-slate-900 mb-1">{t.noChangesYet}</h3>
          <p className="text-sm text-slate-500 max-w-[240px]">
            {t.changesDescription}
          </p>
        </div>
      ) : (
        <ScrollArea className="flex-1">
          <div className="p-4 space-y-3" data-testid="changes-timeline">
            {changes.map((change, index) => {
              const Icon = getIcon(change.type);
              return (
                <Card 
                  key={change.id || index} 
                  className="p-4 border-slate-200 hover:border-slate-300 transition-colors shadow-sm"
                  data-testid="changes-item"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4 text-slate-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="secondary" className="text-xs font-normal bg-violet-100 text-violet-700">
                          {getTypeLabel(change.type)}
                        </Badge>
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(change.timestamp)} {formatTime(change.timestamp)}
                        </span>
                      </div>
                      <p className="text-sm text-slate-700">{change.description}</p>
                      {change.new && (
                        <p className="text-xs text-slate-500 mt-1 truncate">
                          {change.new}
                        </p>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </ScrollArea>
      )}
    </div>
  );
};
export default ChangesTab;
